import { isMindsetApiError } from "@mindset/api-client";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, Check, CreditCard, Minus, RefreshCw } from "lucide-react";
import type { MindsetCheckoutInput, MindsetCheckoutPlan, MindsetCheckoutSession } from "@mindset/api-client";
import type { IntegrationCheckoutClient } from "./integrationSetupClient";

interface Props { companyId: string; userId: string; client: IntegrationCheckoutClient; onChanged?: () => void;
  onEnvironmentChanged?: (environment: CheckoutEnvironment) => void }
interface Intent { key: string; checkout: MindsetCheckoutInput; operationId?: string }
type CheckoutEnvironment = MindsetCheckoutInput["environment"];
type EnvironmentAvailability = { status: "checking" | "available" | "unavailable" | "partner"; reason?: string };
const checkoutEnvironments = ["sandbox", "production"] as const;
export function readCheckoutIntent(raw: string | null): Intent | null {
  if (raw === null) return null;
  if (raw.length > 4096) throw new Error("Invalid saved checkout");
  const parsed: unknown = JSON.parse(raw);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("Invalid saved checkout");
  const value = parsed as Record<string, unknown>;
  if (Object.keys(value).some(key => !["key", "checkout", "operationId"].includes(key)) ||
    typeof value.key !== "string" || !/^[A-Za-z0-9_-]{8,80}$/.test(value.key) ||
    !value.checkout || typeof value.checkout !== "object" || Array.isArray(value.checkout) ||
    (value.operationId !== undefined && (typeof value.operationId !== "string" ||
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.operationId)))) throw new Error("Invalid saved checkout");
  const checkout = value.checkout as Record<string, unknown>;
  if (Object.keys(checkout).some(key => !["environment", "planKey", "planVersion", "businessHandleQuantity"].includes(key)) ||
    (checkout.environment !== "sandbox" && checkout.environment !== "production") || typeof checkout.planKey !== "string" ||
    !/^[a-z][a-z0-9-]{1,78}[a-z0-9]$/.test(checkout.planKey) || typeof checkout.planVersion !== "number" ||
    !Number.isSafeInteger(checkout.planVersion) || checkout.planVersion < 1 ||
    (checkout.businessHandleQuantity !== undefined && !validHandleQuantity(checkout.businessHandleQuantity))) throw new Error("Invalid saved checkout");
  return { key: value.key, checkout: {
    environment: checkout.environment, planKey: checkout.planKey, planVersion: checkout.planVersion,
    ...(checkout.businessHandleQuantity === undefined ? {} : { businessHandleQuantity: checkout.businessHandleQuantity as number }),
  }, ...(value.operationId === undefined ? {} : { operationId: value.operationId as string }) };
}
function validHandleQuantity(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 1 && value <= 100000;
}
export function IntegrationCheckout(props: Props) {
  return <CheckoutEnvironmentSelection key={`${props.companyId}:${props.userId}`} {...props} />;
}
function CheckoutEnvironmentSelection(props: Props) {
  const [environment, setEnvironment] = useState<CheckoutEnvironment | null>(null);
  const [availabilityVersion, setAvailabilityVersion] = useState(0);
  const [availability, setAvailability] = useState<Record<CheckoutEnvironment, EnvironmentAvailability>>({
    sandbox: { status: "checking" },
    production: { status: "checking" },
  });
  useEffect(() => {
    let current = true;
    setAvailability({ sandbox: { status: "checking" }, production: { status: "checking" } });
    for (const value of checkoutEnvironments) {
      void props.client.checkoutPlans(props.companyId, props.userId, value).then((items) => {
        if (!current) return;
        const verified = items.length > 0 && items.every(plan => plan.environment === value);
        setAvailability((prior) => ({ ...prior, [value]: verified ? { status: "available" } :
          { status: "unavailable", reason: value === "production" ? "Production checkout has no verified plans available." :
            "Sandbox checkout plans are unavailable." } }));
      }, (error: unknown) => {
        if (!current) return;
        if (isMindsetApiError(error) && error.code === "OWNED_PARTNER_NO_CHECKOUT" && error.status === 409) {
          setAvailability(prior => ({ ...prior, [value]: { status: "partner" } }));
          return;
        }
        setAvailability((prior) => ({ ...prior, [value]: { status: "unavailable",
          reason: value === "production" ? "Production checkout could not be verified. Refresh plans to retry." :
            "Sandbox checkout plans are unavailable." } }));
      });
    }
    return () => { current = false; };
  }, [props.client, props.companyId, props.userId, availabilityVersion]);
  useEffect(() => {
    // Resolve the default before mounting a checkout; never switch an in-progress
    // environment automatically when a later catalog refresh fails.
    if (environment !== null || availability.production.status === "checking") return;
    if (["available", "partner"].includes(availability.production.status)) setEnvironment("production");
    else if (availability.sandbox.status !== "checking") setEnvironment("sandbox");
  }, [availability, environment]);
  useEffect(() => {
    if (environment) props.onEnvironmentChanged?.(environment);
  }, [environment, props.onEnvironmentChanged]);
  return <>
    <div className="integration-checkout-environments" role="group" aria-label="Subscription environment">
      {checkoutEnvironments.map(value => <button key={value} type="button" aria-pressed={environment === value}
        disabled={!["available", "partner"].includes(availability[value].status) && environment !== value}
        title={availability[value].reason}
        onClick={() => setEnvironment(value)}>{value === "sandbox" ? "Sandbox" : "Production"}</button>)}
    </div>
    {availability.production.status === "unavailable" ? <p className="integration-availability" role="status">{availability.production.reason}</p> : null}
    {availability.production.status === "checking" ? <p className="integration-availability" role="status">Checking production checkout availability...</p> : null}
    {availability.sandbox.status === "unavailable" ? <p className="integration-availability" role="status">{availability.sandbox.reason}</p> : null}
    {environment && availability[environment].status === "partner" ? <section className="operational-panel" aria-label="Partner brand access">
      <h3>Partner brand access</h3><p>Company access, business handles and recurring subscriptions are $0. No payment is required.</p>
      <p>Continue company and integration setup. Domain ownership and security checks still apply.</p>
    </section> : environment ? <CheckoutWorkspace key={`${props.companyId}:${props.userId}:${environment}`} {...props} environment={environment}
      onRefreshAvailability={() => setAvailabilityVersion((value) => value + 1)} /> : null}
  </>;
}
function CheckoutWorkspace({ companyId, userId, client, environment, onChanged, onRefreshAvailability }: Props & {
  environment: CheckoutEnvironment;
  onRefreshAvailability?: () => void;
}) {
  // Keep the original sandbox key so uncertain pre-upgrade requests remain recoverable.
  const storageKey = `mindset-checkout:${encodeURIComponent(companyId)}:${encodeURIComponent(userId)}${environment === "production" ? ":production" : ""}`;
  const [plans, setPlans] = useState<MindsetCheckoutPlan[]>([]), [loading, setLoading] = useState(true);
  const [handleQuantity, setHandleQuantity] = useState("1");
  const [error, setError] = useState(""), [busy, setBusy] = useState(false), [version, setVersion] = useState(0);
  const [storageFailed, setStorageFailed] = useState(false);
  const [recovering, setRecovering] = useState(true), [recoveryError, setRecoveryError] = useState("");
  const [intent, setIntent] = useState<Intent | null>(null), [session, setSession] = useState<MindsetCheckoutSession | null>(null);
  const [recoverySessionId, setRecoverySessionId] = useState(""), [recoveryConfirmed, setRecoveryConfirmed] = useState(false);
  const alive = useRef(false), working = useRef(false);
  const savedIntent = useRef<Intent | null>(null);
  const lastSessionStatus = useRef<MindsetCheckoutSession["status"] | null>(null);
  useEffect(() => {
    alive.current = true;
    try {
      const saved = readCheckoutIntent(sessionStorage.getItem(storageKey));
      if (saved) {
        if (saved.checkout.environment !== environment) throw new Error("Saved checkout environment mismatch");
        savedIntent.current = saved;
        setIntent(savedIntent.current);
      }
    } catch { setStorageFailed(true); setError("Saved checkout could not be recovered. Reload before continuing."); }
    return () => { alive.current = false; };
  }, [storageKey]);
  useEffect(() => {
    let current = true;
    setRecovering(true); setRecoveryError("");
    void (async () => {
      let found = false;
      try {
        const operation = await client.currentCheckout(companyId, userId, environment);
        if (!current) return;
        if (!operation) {
          // History omits expired operations; verify a persisted ID rather than
          // leaving its previously open payment link visible until the next poll.
          const savedId = savedIntent.current?.operationId;
          if (savedId) {
            found = true;
            const saved = savedIntent.current!;
            const existing = await client.getCheckout(companyId, userId, savedId);
            if (!current) return;
            if (existing.companyId !== companyId || existing.environment !== environment || existing.planKey !== saved.checkout.planKey || existing.planVersion !== saved.checkout.planVersion ||
              existing.businessHandleQuantity !== saved.checkout.businessHandleQuantity)
              throw new Error("Saved checkout scope mismatch");
            const observed = await client.refreshCheckout(companyId, userId, savedId);
            if (current) setSession(observed);
          }
          return;
        }
        if (operation.companyId !== companyId || operation.environment !== environment) throw new Error("Checkout scope mismatch");
        // An unrelated current checkout cannot resolve a request whose response
        // was lost. Replay the saved key before adopting server history.
        if (savedIntent.current && !savedIntent.current.operationId) return;
        found = true;
        const recovered: Intent = { key: crypto.randomUUID(), operationId: operation.id,
          checkout: { environment, planKey: operation.planKey, planVersion: operation.planVersion,
            ...(operation.businessHandleQuantity === undefined ? {} : { businessHandleQuantity: operation.businessHandleQuantity }) } };
        persist(recovered); setSession(null); setStorageFailed(false); setError("");
        const observed = await client.refreshCheckout(companyId, userId, operation.id);
        if (current) setSession(observed);
      } catch {
        if (current) {
          setSession(null);
          if (found) setError("Checkout could not be updated. Retry this checkout.");
          else setRecoveryError("Existing checkout could not be checked. Refresh before choosing a plan.");
        }
      } finally { if (current) setRecovering(false); }
    })();
    return () => { current = false; };
  }, [client, companyId, userId, environment, version]);
  useEffect(() => {
    let current = true; setLoading(true);
    void client.checkoutPlans(companyId, userId, environment).then((items) => {
      if (current) {
        if (items.some(plan => plan.environment !== environment)) setError("Subscription plan environment could not be verified.");
        else setPlans(items);
      }
    },
      () => { if (current) setError("Subscription plans are unavailable."); }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [client, companyId, userId, environment, version]);
  function persist(value: Intent) {
    if (value.checkout.environment !== environment) throw new Error("Checkout environment mismatch");
    try { sessionStorage.setItem(storageKey, JSON.stringify(value)); savedIntent.current = value; setIntent(value); }
    catch { setStorageFailed(true); throw new Error("Checkout storage unavailable"); }
  }
  function commitSession(observed: MindsetCheckoutSession) {
    setSession(observed);
    if (lastSessionStatus.current !== observed.status) {
      lastSessionStatus.current = observed.status;
      onChanged?.();
    }
  }
  async function advance(value: Intent) {
    if (working.current || storageFailed || recovering || recoveryError) return;
    working.current = true; setBusy(true); setError("");
    try {
      persist(value);
      const operation = value.operationId ? await client.getCheckout(companyId, userId, value.operationId)
        : await client.requestCheckout(companyId, userId, value.key, value.checkout);
      if (!alive.current) return;
      if (operation.companyId !== companyId || operation.environment !== environment || operation.planKey !== value.checkout.planKey || operation.planVersion !== value.checkout.planVersion ||
        operation.businessHandleQuantity !== value.checkout.businessHandleQuantity)
        throw new Error("Checkout scope mismatch");
      const recovered = { ...value, operationId: operation.id }; persist(recovered);
      const observed = await client.refreshCheckout(companyId, userId, operation.id);
      if (alive.current) commitSession(observed);
    } catch { if (alive.current) { setSession(null); setError("Checkout could not be updated. Retry this checkout."); } }
    finally { working.current = false; if (alive.current) setBusy(false); }
  }
  useEffect(() => {
    if (!intent?.operationId || busy || recovering || recoveryError || error || session?.status === "complete" || session?.status === "expired" || session?.status === "reconciliation_required") return;
    const timer = setTimeout(() => { void advance(intent); }, 5000);
    return () => clearTimeout(timer);
  }, [intent, busy, recovering, recoveryError, error, session]);
  useEffect(() => {
    if (!intent?.operationId || busy || recovering || recoveryError || storageFailed ||
      session?.status === "complete" || session?.status === "expired" || session?.status === "reconciliation_required") return;
    const refresh = () => {
      if (document.visibilityState === "visible") void advance(intent);
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [intent, busy, recovering, recoveryError, storageFailed, session]);
  async function recoverExisting() {
    const recover = client.recoverCheckout;
    if (!recover || !intent?.operationId || session?.status !== "reconciliation_required" || !recoveryConfirmed ||
      !recoveryPattern.test(recoverySessionId) || recoverySessionId.length > 255 || working.current || recovering || storageFailed) return;
    working.current = true; setBusy(true); setError("");
    try {
      await recover(companyId, userId, intent.operationId, recoverySessionId, true);
      if (!alive.current) return;
      setRecoverySessionId(""); setRecoveryConfirmed(false);
      const observed = await client.refreshCheckout(companyId, userId, intent.operationId);
      if (alive.current) commitSession(observed);
    } catch { if (alive.current) setError("Existing checkout could not be confirmed. Check status before retrying."); }
    finally { working.current = false; if (alive.current) setBusy(false); }
  }
  const labels = { pending: "Preparing checkout", open: "Ready for payment", processing: "Payment processing", complete: "Payment verified. Activation pending.", expired: "Checkout expired", reconciliation_required: "Checkout needs review" };
  const recoveryPattern = environment === "sandbox" ? /^cs_test_[A-Za-z0-9]+$/ : /^cs_live_[A-Za-z0-9]+$/;
  return <section className="integration-checkout" aria-labelledby="subscription-title">
    <header className="integration-applications-heading"><h3 id="subscription-title">Subscription <small>{environment === "sandbox" ? "Sandbox" : "Production"}</small></h3>
      <button className="portal-icon-button" title="Refresh plans" aria-label="Refresh plans" disabled={loading || busy || recovering} onClick={() => {
        setError(""); setVersion((v) => v + 1); onRefreshAvailability?.();
      }}><RefreshCw size={17} /></button></header>
    {loading ? <p role="status">Loading plans...</p> : null}
    {recovering ? <p role="status">Checking existing checkout...</p> : null}
    {recoveryError ? <p role="alert">{recoveryError}</p> : null}
    {error ? <p role="alert">{error}</p> : null}
    {!intent && !loading && !plans.length ? <p>No subscription plans available.</p> : null}
    {!intent ? <ul className="integration-plan-list">{plans.map((plan) => {
      const annual = plan.billingModelVersion === "mindset.subscription-components/v2";
      const quantity = Number(handleQuantity);
      const validQuantity = validHandleQuantity(quantity);
      return <li key={`${plan.planKey}:${plan.planVersion}`}>
      <div><strong>{plan.price.displayName}</strong><p>{annual ? validQuantity ? formatCheckoutPrice({ ...plan.price, unitAmount: 3600 + 1200 * quantity }) : "Enter a valid handle quantity" : formatCheckoutPrice(plan.price)} / {plan.price.intervalCount > 1 ? `${plan.price.intervalCount} ` : ""}{plan.price.interval}{plan.price.intervalCount > 1 ? "s" : ""}</p>
        {annual ? <label className="integration-handle-quantity">Business-owned @handles<input type="number" min={1} max={100000} step={1} value={handleQuantity}
          disabled={busy || recovering} onChange={event => setHandleQuantity(event.target.value)} /></label> : null}
        {annual ? <p>$36/year per company + $12/year per business-owned @handle. Individuals are free. Existing subscriptions are unchanged.</p> : null}
        {plan.price.taxBehavior !== "unspecified" ? <small>{plan.price.taxBehavior === "inclusive" ? "Tax included" : "Excludes tax"}</small> : null}
        {plan.commercialPricing ? <dl className="integration-commercial-pricing" aria-label={`${plan.price.displayName} commercial pricing`}>
          <div><dt>Individuals</dt><dd>{formatCommercialAmount(plan.commercialPricing.individuals.unitAmount, plan.commercialPricing.individuals.currency)} {plan.commercialPricing.individuals.cadence}</dd></div>
          <div><dt>Business @ identities</dt><dd>{formatCommercialAmount(plan.commercialPricing.businessAtIdentity.unitAmount, plan.commercialPricing.businessAtIdentity.currency)} / {plan.commercialPricing.businessAtIdentity.interval}</dd></div>
          <div><dt>Workspace</dt><dd>{formatCommercialAmount(plan.commercialPricing.businessCommunicationsWorkspace.unitAmount, plan.commercialPricing.businessCommunicationsWorkspace.currency)} / {plan.commercialPricing.businessCommunicationsWorkspace.interval} flat</dd></div>
        </dl> : null}
        <dl className="integration-plan-features" aria-label={`${plan.price.displayName} features`}>
          {([["messaging", "Messaging"], ["audioCalls", "Voice calls"], ["videoCalls", "Video calls"]] as const).map(([key, label]) => {
            const included = plan.capabilities[key] === true;
            return <div key={key}><dt>{label}</dt><dd>{included ? <Check size={14} aria-hidden="true" /> : <Minus size={14} aria-hidden="true" />}
              {included ? "Included" : "Not included"}</dd></div>;
          })}
        </dl></div>
      <button className="portal-button portal-button--primary" disabled={loading || recovering || Boolean(recoveryError) || busy || Boolean(error) || storageFailed || (annual && !validQuantity)} onClick={() => void advance({ key: crypto.randomUUID(), checkout: { environment, planKey: plan.planKey, planVersion: plan.planVersion,
        ...(annual ? { businessHandleQuantity: quantity } : {}) } })}><CreditCard size={16} />Choose plan</button>
    </li>; })}</ul> : <div className="integration-checkout-progress">
      {intent.checkout.businessHandleQuantity !== undefined ? <p>{intent.checkout.businessHandleQuantity} business-owned @handles</p> : null}
      <p role="status">{session ? labels[session.status] : busy ? "Updating checkout..." : "Checkout awaiting verification"}</p>
      {session?.status === "reconciliation_required" && client.recoverCheckout ? <form className="integration-checkout-recovery" onSubmit={(event) => { event.preventDefault(); void recoverExisting(); }}>
        <label>Existing Stripe {environment === "sandbox" ? "test" : "live"} session<input value={recoverySessionId} maxLength={255} autoComplete="off" spellCheck={false} disabled={busy || recovering}
          onChange={(event) => { setRecoverySessionId(event.target.value); setRecoveryConfirmed(false); }} /></label>
        <label className="integration-checkout-confirmation"><input type="checkbox" checked={recoveryConfirmed} disabled={busy || recovering}
          onChange={(event) => setRecoveryConfirmed(event.target.checked)} />Attach this existing session to this checkout</label>
        <button type="submit" className="portal-button" disabled={busy || recovering || storageFailed || !recoveryConfirmed || !recoveryPattern.test(recoverySessionId)}><RefreshCw size={16} />Recover existing checkout</button>
      </form> : null}
      {session?.status === "open" && session.checkoutUrl ? <a className="portal-button portal-button--primary" href={session.checkoutUrl}>Continue to payment<ArrowRight size={16} /></a> : null}
      {session?.status !== "complete" && session?.status !== "expired" ? <button className="portal-button" disabled={busy || recovering || Boolean(recoveryError) || storageFailed} onClick={() => void advance(intent)}><RefreshCw size={16} />{error && !intent.operationId ? "Retry checkout" : "Check status"}</button> : null}
      {session?.status === "expired" ? <button className="portal-button" onClick={() => { try { sessionStorage.removeItem(storageKey); savedIntent.current = null; setIntent(null); setSession(null); } catch { setError("Checkout storage is unavailable."); } }}>Choose another plan</button> : null}
    </div>}
  </section>;
}
export function formatCheckoutPrice(price: MindsetCheckoutPlan["price"]): string {
  const formatter = new Intl.NumberFormat(undefined, { style: "currency", currency: price.currency });
  const digits = ["isk", "ugx"].includes(price.currency) ? 2 : formatter.resolvedOptions().maximumFractionDigits;
  if (digits === undefined) throw new Error("Currency precision is unavailable");
  return formatter.format(price.unitAmount / 10 ** digits);
}

function formatCommercialAmount(unitAmount: number, currency: string): string {
  return formatCheckoutPrice({
    displayName: "Commercial amount",
    unitAmount,
    currency,
    interval: "month",
    intervalCount: 1,
    taxBehavior: "unspecified",
  });
}
